import {
  ArcType,
  Axis,
  CallbackPositionProperty,
  CallbackProperty,
  Cartesian2,
  Cartesian3,
  Math as CesiumMath,
  Color,
  ConstantPositionProperty,
  Entity,
  JulianDate,
  PolylineGlowMaterialProperty,
  Ray,
  SceneTransforms,
  Viewer,
} from "cesium";
import proj4 from "proj4";
import { generateTextElement } from "../utils/generateTextElement";
import { getDirectionMatrix } from "../utils/getDirectionMatrix";
import { SurfaceUtils } from "../utils/SurfaceUtils";
import { VectorUtils } from "../utils/VectorUtils";
import CameraPlugin from "./CameraPlugin";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { ActivatableToolInterface } from "./ActivatableToolInterface";
import { BackgroundableToolInterface } from "./BackgroundableToolInterface";
import {
  SerializableToolInterface,
  compareToolState,
} from "./SerializableToolInterface";
import EventEmitter, { EventEmitterInterface } from "./EventEmitter";
import PositionRayPickerPlugin, {
  PositionEvent,
} from "./PositionRayPickerPlugin";
import { ProjectNorthDirection } from "components/modules/Projects/Controls/ProjectNorth/types/ProjectNorthDirection";
import { orientDD } from "components/modules/Projects/Controls/ProjectNorth/utils/orient-dd";
import {
  ObservableProperty,
  DerivedObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";

export type UnitModel = {
  factor: number;
  shortname: string;
  fullname: string;
  decimals: number;
};

export const linearUnits: Readonly<UnitModel>[] = [
  { factor: 1000, shortname: "mm", fullname: "millimetres", decimals: 0 },
  { factor: 1, shortname: "m", fullname: "metres", decimals: 3 },
  { factor: 0.001, shortname: "km", fullname: "kilometres", decimals: 3 },
];

type ChangeEvent = { value: number; text: string };
type ToolStage = "pickingStart" | "pickingEnd" | "complete";

export type SnapPreset =
  | "free-movement"
  | "snap-to-project-grid"
  | "snap-to-surface-normal";

export type MeasurementToolSnapshot =
  | MeasurementToolSnapshotV1
  | MeasurementToolSnapshotV2;
type MeasurementToolSnapshotV1 = {
  version?: 1;
  toolStage: ToolStage;
  points: { x: number; y: number; z: number }[];
  unit: {
    decimals: number;
    factor: number;
    fullname: string;
    shortname: string;
  };
  axisSnapEnabled: boolean;
  snapToOpposingSurface: boolean;
  surfaceDirectionSnapEnabled: boolean;
  surfacePointOppositeNormal: Cartesian3 | null;
};
type MeasurementToolSnapshotV2 = {
  version: 2;
  points: { x: number; y: number; z: number }[];
  unitShortName: string;
};

function migrateState(
  state: MeasurementToolSnapshot | undefined,
): MeasurementToolSnapshotV2 | undefined {
  if (!state || state.version === 2) return state as MeasurementToolSnapshotV2;
  return {
    version: 2,
    points: state.points,
    unitShortName:
      (state as unknown as MeasurementToolSnapshotV2).unitShortName ?? "mm",
  };
}

export function isMeasureStateApplied(
  state: MeasurementToolSnapshot | undefined,
): boolean {
  state = migrateState(state);
  return (state?.points?.length ?? 0) > 0;
}

export default class MeasureToolPlugin
  implements
    AttachablePluginInterface,
    ActivatableToolInterface,
    SerializableToolInterface<MeasurementToolSnapshot>,
    BackgroundableToolInterface
{
  private _viewer: Viewer = null;
  private _isActive = false;
  private _changeEvent = new EventEmitter<ChangeEvent>();
  private _unit: UnitModel = linearUnits[0];
  private _axisSnapEnabled = false;
  private _axisSnapGrid: {
    converter: proj4.Converter;
    northDegrees: number;
  } = null;
  private _surfaceDirectionSnapEnabled = false;
  private _surfacePointOppositeNormal: Cartesian3 | null = null;
  private _entityLine: Entity = null;
  private _entityLabel: Entity = null;
  private _elementLabelText: HTMLElement = null;
  private _detachClickHandlerFunc: () => void;
  private _detachMoveHandlerFunc: () => void;
  private _detachPointerMoveHandlerFunc: () => void;
  private _detachPostRenderHandlerLabelUpdateFunc: () => void;
  private _selectedSurfaceNormal: Cartesian3 | null = null;
  private _selectedSurfaceNormalAbortController: AbortController | null = null;
  private _selectedSurfaceNormalRequestVersion = 0;
  private _measurementStartEvent: PositionEvent | null = null;
  private _mouseMoveEvent: PositionEvent;
  private _latestMouseScreenPosition: Cartesian2 | null = null;
  private _pendingPointerEndPointUpdate: number | null = null;
  private _pendingPointerIdleUpdate: ReturnType<typeof setTimeout> | null =
    null;
  private readonly _pointerIdlePickDelayMs = 75;
  private _entityCursorPoint: Entity;
  private _entityMeasurementStart: Entity;
  private _entityMeasurementEnd: Entity;
  private _surfaceUtils: SurfaceUtils;

  private readonly _points = new ObservableProperty<Cartesian3[]>([]);
  private readonly _toolStage: ObservableProperty<ToolStage> =
    new ObservableProperty("pickingStart");
  private readonly _measurementValueText = new ObservableProperty<string>("0");
  private readonly _linearUnit = new ObservableProperty<Readonly<UnitModel>>(
    linearUnits[0],
  );
  private readonly _snapPreset = new ObservableProperty<SnapPreset>(
    "free-movement",
  );
  private readonly _autoSnapToOpposingSurface = new ObservableProperty<boolean>(
    false,
  );
  readonly isApplied = new DerivedObservableProperty(
    () => isMeasureStateApplied(this.capture()),
    [this._points, this._toolStage],
  );

  get changeEvent(): EventEmitterInterface<ChangeEvent> {
    return this._changeEvent;
  }

  get isAxisSnapEnabled() {
    return this._axisSnapEnabled;
  }

  get toolStage(): ReadonlyObservableProperty<ToolStage> {
    return this._toolStage;
  }

  get measurementValueText(): ReadonlyObservableProperty<string> {
    return this._measurementValueText;
  }

  get linearUnit(): ReadonlyObservableProperty<Readonly<UnitModel>> {
    return this._linearUnit;
  }

  get snapPreset(): ReadonlyObservableProperty<SnapPreset> {
    return this._snapPreset;
  }

  get autoSnapToOpposingSurface(): ReadonlyObservableProperty<boolean> {
    return this._autoSnapToOpposingSurface;
  }

  constructor(
    private _positionPicker: PositionRayPickerPlugin,
    private readonly _camera: CameraPlugin,
  ) {}

  setViewer(viewer: Viewer) {
    this._viewer = viewer;
    this._surfaceUtils = new SurfaceUtils(viewer);
  }

  detachViewer() {
    this._viewer = null;
  }

  setActive(isToolActive: boolean) {
    if (!this._viewer) return;

    this._isActive = isToolActive;

    if (isToolActive) {
      this.attachPointerMoveHandler();
      this.attachClickHandler(this._positionPicker.leftClickEvent);
      this.attachMoveHandler(this._positionPicker.moveEvent);
    } else {
      // A finished measurement stays on screen while another tool is used
      // (it keeps running in the background until Reset or its pill closes
      // it). A half-finished one, or just the cursor preview, is cleared.
      if (this._toolStage.getSnapshot() === "complete") {
        this.removeEntityCursorPoint();
      } else {
        this.clear();
      }
      this._detachClickHandlerFunc?.();
      this._detachMoveHandlerFunc?.();
      this._detachPointerMoveHandlerFunc?.();
      this.cancelPendingPointerEndPointUpdate();
      this.cancelPendingPointerIdleUpdate();
      this._viewer?.scene.requestRender();
    }
  }

  setUnits(unit: UnitModel) {
    this._unit = unit ?? linearUnits[0];
    this._linearUnit.set(this._unit);
    this.updateMeasurementLabelValue();
    this._viewer?.scene.requestRender();
  }

  cycleLinearUnit() {
    const currentIndex = linearUnits.findIndex(
      (u) => u.shortname === this._unit.shortname,
    );
    const nextIndex = (currentIndex + 1) % linearUnits.length;
    this.setUnits(linearUnits[nextIndex]);
  }

  setSnapPreset(preset: SnapPreset) {
    this._snapPreset.set(preset);
    this.setProjectGridSnapEnabled(preset === "snap-to-project-grid");
    this.setSurfaceDirectionSnapEnabled(preset === "snap-to-surface-normal");
  }

  setProjectGridSnapEnabled(value: boolean) {
    this._axisSnapEnabled = value ?? false;
  }

  setSurfaceDirectionSnapEnabled(value: boolean) {
    const surfaceDirectionSnapEnabled = value ?? false;
    if (surfaceDirectionSnapEnabled === this._surfaceDirectionSnapEnabled)
      return;

    this._surfaceDirectionSnapEnabled = surfaceDirectionSnapEnabled;

    if (!this._surfaceDirectionSnapEnabled) {
      this.abortSelectedSurfaceNormalUpdate();
      this._selectedSurfaceNormal = null;
      return;
    }

    if (
      this._toolStage.getSnapshot() === "pickingEnd" &&
      !this._selectedSurfaceNormal &&
      this._measurementStartEvent
    ) {
      this.setInitialSelectedSurfaceNormal(this._measurementStartEvent);
      this.refreshEndPointFromLatestMousePosition();
    }

    if (
      this._toolStage.getSnapshot() === "pickingEnd" &&
      !this._selectedSurfaceNormalAbortController &&
      this._measurementStartEvent
    ) {
      void this.updateSelectedSurfaceNormalAsync(this._measurementStartEvent);
    }
  }

  setSnapToOpposingSurfaceEnabled(value: boolean) {
    // Note:
    // The react-hotkeys-hook library will trigger this rapidly while a key is held down.
    // Check value first to avoid unnecessary updates.
    if (value === this._autoSnapToOpposingSurface.getSnapshot()) return;
    this._autoSnapToOpposingSurface.set(value ?? false);

    if (!this._surfaceDirectionSnapEnabled) return;

    if (!this._autoSnapToOpposingSurface.getSnapshot()) {
      this._surfacePointOppositeNormal = null;
    }

    // If the measurement is complete, don't update the second point position.
    if (this._toolStage.getSnapshot() === "complete") return;

    const points = this._points.getSnapshot();
    if (
      this._toolStage.getSnapshot() !== "pickingEnd" ||
      !points[0] ||
      !this._mouseMoveEvent?.screenPosition
    ) {
      return;
    }

    if (!this._selectedSurfaceNormal && this._measurementStartEvent) {
      this.setInitialSelectedSurfaceNormal(this._measurementStartEvent);
    }

    if (
      !this._selectedSurfaceNormalAbortController &&
      this._measurementStartEvent
    ) {
      void this.updateSelectedSurfaceNormalAsync(this._measurementStartEvent);
    }

    if (
      this._autoSnapToOpposingSurface.getSnapshot() &&
      !this._surfacePointOppositeNormal
    ) {
      this._surfacePointOppositeNormal = this.getOpposingSurfaceSnapPosition(
        points[0],
      );
    }

    // Otherwise update the second point position when the snapping option is enabled.
    const snappedPoint =
      this._autoSnapToOpposingSurface.getSnapshot() &&
      this._surfacePointOppositeNormal
        ? this._surfacePointOppositeNormal
        : this.getSurfaceDirectionSnapPosition(
            points[0],
            this._mouseMoveEvent.screenPosition,
          );
    const nextPoint0 = points[0];
    const nextPoint1 =
      snappedPoint ?? this._mouseMoveEvent.position ?? points[1];
    this._points.set([nextPoint0, nextPoint1]);

    if (nextPoint1) {
      this.updateMeasurementLabelValue();
      this.updateLabelPosition();
      this._viewer.scene.requestRender();
    }
  }

  setProjectGridSnapAxis(
    crsProjText: string,
    projectNorthDegrees: number,
    projectNorthDirection: ProjectNorthDirection,
  ) {
    if (!crsProjText) {
      this._axisSnapGrid = null;
      return;
    }

    //create converter for WGS84 to project CRS
    proj4.defs(
      "EPSG:4978",
      "+proj=geocent +datum=WGS84 +units=m +no_defs +type=crs",
    );
    const converter = proj4("EPSG:4978", crsProjText);

    // Convert west angles to east equivalent
    const orientedDegrees = orientDD(
      projectNorthDegrees,
      projectNorthDirection,
    );

    this._axisSnapGrid = {
      converter,
      northDegrees: orientedDegrees || 90,
    };
  }

  private attachPointerMoveHandler() {
    this._detachPointerMoveHandlerFunc?.();

    const canvas = this._viewer?.canvas;
    if (!canvas) return;

    const handlePointerMove = (ev: PointerEvent) => {
      const canvasBounds = canvas.getBoundingClientRect();
      this._latestMouseScreenPosition = new Cartesian2(
        ev.clientX - canvasBounds.left,
        ev.clientY - canvasBounds.top,
      );
      this.schedulePointerUpdateFromLatestPosition();
      this.scheduleIdlePointerUpdateFromLatestPosition();
    };

    canvas.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    this._detachPointerMoveHandlerFunc = () => {
      canvas.removeEventListener("pointermove", handlePointerMove);
    };
  }

  private schedulePointerUpdateFromLatestPosition() {
    if (
      !this._isActive ||
      this._toolStage.getSnapshot() === "complete" ||
      this._pendingPointerEndPointUpdate !== null
    ) {
      return;
    }

    const updatePosition = () => {
      this._pendingPointerEndPointUpdate = null;
      this.updateFromLatestPointerPosition();
    };

    if (typeof requestAnimationFrame === "function") {
      this._pendingPointerEndPointUpdate =
        requestAnimationFrame(updatePosition);
      return;
    }

    this._pendingPointerEndPointUpdate = window.setTimeout(updatePosition, 0);
  }

  private scheduleIdlePointerUpdateFromLatestPosition() {
    if (!this._isActive || this._toolStage.getSnapshot() === "complete") {
      return;
    }

    this.cancelPendingPointerIdleUpdate();
    this._pendingPointerIdleUpdate = setTimeout(() => {
      this._pendingPointerIdleUpdate = null;
      this.updateFromLatestPointerPosition(true);
    }, this._pointerIdlePickDelayMs);
  }

  private cancelPendingPointerEndPointUpdate() {
    if (this._pendingPointerEndPointUpdate === null) return;

    if (typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this._pendingPointerEndPointUpdate);
    } else {
      clearTimeout(this._pendingPointerEndPointUpdate);
    }
    this._pendingPointerEndPointUpdate = null;
  }

  private cancelPendingPointerIdleUpdate() {
    if (!this._pendingPointerIdleUpdate) return;

    clearTimeout(this._pendingPointerIdleUpdate);
    this._pendingPointerIdleUpdate = null;
  }

  private attachClickHandler(
    clickEvent: typeof this._positionPicker.leftClickEvent,
  ) {
    this._detachClickHandlerFunc?.();
    this._detachClickHandlerFunc = clickEvent.addHandler((ev) => {
      if (!this._viewer || !this._isActive) return;
      const worldPosition = ev.position;
      const screenPosition = ev.screenPosition;
      if (!worldPosition) return;

      if (
        this._toolStage.getSnapshot() === "pickingStart" ||
        this._toolStage.getSnapshot() === "complete"
      ) {
        this._toolStage.set("pickingEnd");
        this._selectedSurfaceNormal = null;
        this._surfacePointOppositeNormal = null;

        // Detailed surface normals require many offscreen ray picks. Only do
        // that work when surface-normal snapping actually needs it; free
        // movement and project-grid snapping should enter measurement mode
        // immediately on large tilesets.
        this.abortSelectedSurfaceNormalUpdate();
        this._measurementStartEvent = this.clonePositionEvent(ev);
        this._points.set([worldPosition]);

        if (this._surfaceDirectionSnapEnabled) {
          this.setInitialSelectedSurfaceNormal(this._measurementStartEvent);
          void this.updateSelectedSurfaceNormalAsync(
            this._measurementStartEvent,
          );
        }

        if (!this._entityMeasurementStart) {
          this._entityMeasurementStart = this.addMeasurementEntityStart();
        }
      } else if (this._toolStage.getSnapshot() === "pickingEnd") {
        this._toolStage.set("complete");
        const points = this._points.getSnapshot();
        const startPosition = points[0];
        let endPosition: Cartesian3;
        if (this._axisSnapEnabled && this._axisSnapGrid) {
          endPosition =
            this.getProjectGridAxisSnapPosition(
              worldPosition,
              startPosition,
              screenPosition,
            ) ?? worldPosition;
        } else if (this._surfaceDirectionSnapEnabled) {
          if (!this._selectedSurfaceNormal) {
            endPosition = points[1] ?? startPosition ?? worldPosition;
          } else if (
            this._autoSnapToOpposingSurface.getSnapshot() &&
            !this._surfacePointOppositeNormal
          ) {
            this._surfacePointOppositeNormal =
              this.getOpposingSurfaceSnapPosition(startPosition);
          }

          if (this._selectedSurfaceNormal) {
            if (
              this._autoSnapToOpposingSurface.getSnapshot() &&
              this._surfacePointOppositeNormal
            ) {
              endPosition = this._surfacePointOppositeNormal;
            } else {
              endPosition =
                this.getSurfaceDirectionSnapPosition(
                  startPosition,
                  ev.screenPosition,
                ) ?? worldPosition;
            }
          }
        } else {
          endPosition = worldPosition;
        }
        this._points.set([startPosition, endPosition]);

        if (!this._entityMeasurementEnd) {
          this._entityMeasurementEnd = this.addMeasurementEntityEnd();
        }
      }

      if (
        this._toolStage.getSnapshot() === "pickingEnd" ||
        this._toolStage.getSnapshot() === "complete"
      ) {
        if (!this._entityLine) this.addEntityLine();
        if (!this._entityLabel) this.addEntityLabel();
        this.updateLabelPosition();
      }

      this._viewer.scene.requestRender();
    });
  }

  private attachMoveHandler(moveEvent: typeof this._positionPicker.moveEvent) {
    this._detachMoveHandlerFunc?.();
    this._detachMoveHandlerFunc = moveEvent.addHandler((ev) => {
      if (
        !this._viewer ||
        !this._isActive ||
        !ev.position ||
        !ev.screenPosition
      )
        return;

      this._latestMouseScreenPosition = Cartesian2.clone(
        ev.screenPosition,
        this._latestMouseScreenPosition ?? new Cartesian2(),
      );

      const moveEvent = this._camera.isPositionVisible(ev.position)
        ? ev
        : this.getLatestMouseMoveEvent(true);
      if (!moveEvent) return;

      this._mouseMoveEvent = moveEvent;

      if (!this._entityCursorPoint) {
        this._entityCursorPoint = this.addEntityCursorPoint();
      }

      this._entityCursorPoint.show = true;

      if (this._toolStage.getSnapshot() === "pickingStart") {
        this._points.set([moveEvent.position]);
      } else if (this._toolStage.getSnapshot() === "pickingEnd") {
        this.updateEndPointFromEvent(moveEvent);
        this.ensureMeasurementEndPreviewEntity();
        this.updateMeasurementLabelValue();
        this.updateLabelPosition();
      }
      this._viewer.scene.requestRender();
    });
  }

  private setInitialSelectedSurfaceNormal(ev: PositionEvent) {
    if (
      this._selectedSurfaceNormal ||
      !this._surfaceUtils ||
      !ev?.position ||
      !ev.screenPosition
    ) {
      return;
    }

    try {
      this._selectedSurfaceNormal =
        this._surfaceUtils.getNormalFromDepthSamples(ev) ?? null;
    } catch {
      this._selectedSurfaceNormal = null;
    }
  }

  private async updateSelectedSurfaceNormalAsync(ev: PositionEvent) {
    if (!this._surfaceUtils || !ev?.position || !ev.screenPosition) return;

    this.abortSelectedSurfaceNormalUpdate();

    const abortController = new AbortController();
    const version = ++this._selectedSurfaceNormalRequestVersion;
    this._selectedSurfaceNormalAbortController = abortController;

    const normalEvent = this.clonePositionEvent(ev);

    try {
      let detailedNormal: Cartesian3 | null = null;
      try {
        detailedNormal = await this._surfaceUtils.getNormalDetailedAsync(
          normalEvent,
          abortController.signal,
        );
      } catch {
        detailedNormal = null;
      }

      if (
        abortController.signal.aborted ||
        version !== this._selectedSurfaceNormalRequestVersion
      ) {
        return;
      }

      if (!detailedNormal) return;

      this._selectedSurfaceNormal = detailedNormal;

      await this.waitForNextBrowserFrame();

      if (
        abortController.signal.aborted ||
        version !== this._selectedSurfaceNormalRequestVersion
      ) {
        return;
      }

      this._surfacePointOppositeNormal =
        this._autoSnapToOpposingSurface.getSnapshot()
          ? this.getOpposingSurfaceSnapPosition(this._points.getSnapshot()[0])
          : null;

      this.refreshEndPointFromLatestMousePosition();
    } finally {
      if (this._selectedSurfaceNormalAbortController === abortController) {
        this._selectedSurfaceNormalAbortController = null;
      }
    }
  }

  private abortSelectedSurfaceNormalUpdate() {
    this._selectedSurfaceNormalAbortController?.abort();
    this._selectedSurfaceNormalAbortController = null;
    this._selectedSurfaceNormalRequestVersion += 1;
  }

  private clonePositionEvent(ev: PositionEvent): PositionEvent {
    return {
      ...ev,
      position: ev.position ? Cartesian3.clone(ev.position) : ev.position,
      screenPosition: ev.screenPosition
        ? Cartesian2.clone(ev.screenPosition)
        : ev.screenPosition,
    };
  }

  private async waitForNextBrowserFrame() {
    await new Promise<void>((resolve) => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => resolve());
        return;
      }

      setTimeout(resolve, 0);
    });
  }

  private updateFromLatestPointerPosition(preferRayPick = false) {
    const latestMouseEvent = this.getLatestMouseMoveEvent(preferRayPick);
    if (!latestMouseEvent) return;

    this._mouseMoveEvent = latestMouseEvent;

    if (!this._entityCursorPoint) {
      this._entityCursorPoint = this.addEntityCursorPoint();
    }
    this._entityCursorPoint.show = true;

    if (this._toolStage.getSnapshot() === "pickingStart") {
      this._points.set([latestMouseEvent.position]);
      this._viewer?.scene.requestRender();
      return;
    }

    if (this._toolStage.getSnapshot() === "pickingEnd") {
      this.updateEndPointFromEvent(latestMouseEvent);
      this.ensureMeasurementEndPreviewEntity();
      this.updateMeasurementLabelValue();
      this.updateLabelPosition();
      this._viewer?.scene.requestRender();
    }
  }

  private refreshEndPointFromLatestMousePosition() {
    const latestMouseEvent = this.getLatestMouseMoveEvent();
    if (this._toolStage.getSnapshot() !== "pickingEnd" || !latestMouseEvent) {
      return;
    }

    this._mouseMoveEvent = latestMouseEvent;
    this.updateEndPointFromEvent(latestMouseEvent);
    this.ensureMeasurementEndPreviewEntity();
    this.updateMeasurementLabelValue();
    this.updateLabelPosition();
    this._viewer?.scene.requestRender();
  }

  private getLatestMouseMoveEvent(preferRayPick = false): PositionEvent | null {
    if (!this._latestMouseScreenPosition && !this._mouseMoveEvent) return null;

    const screenPosition = this._latestMouseScreenPosition
      ? Cartesian2.clone(this._latestMouseScreenPosition)
      : this._mouseMoveEvent.screenPosition;
    const fallbackPosition =
      this._mouseMoveEvent?.position &&
      this._camera.isPositionVisible(this._mouseMoveEvent.position)
        ? this._mouseMoveEvent.position
        : null;
    const position =
      this._surfaceDirectionSnapEnabled && fallbackPosition
        ? fallbackPosition
        : this.getWorldPositionAtScreenPosition(
            screenPosition,
            preferRayPick,
          ) ?? fallbackPosition;

    if (!position) return null;

    return {
      ...(this._mouseMoveEvent ?? ({} as PositionEvent)),
      position,
      screenPosition,
    };
  }

  private getWorldPositionAtScreenPosition(
    screenPosition: Cartesian2,
    preferRayPick = false,
  ): Cartesian3 | null {
    if (!this._viewer || !screenPosition) return null;

    const ray = this._viewer.camera.getPickRay(screenPosition);

    if (preferRayPick && ray) {
      const objectsToExclude = this._viewer.entities.values.filter(
        (entity) => entity.properties?.excludeFromPicking,
      );

      try {
        const pickedFromRay = this._viewer.scene.pickFromRay(
          ray,
          objectsToExclude,
          0.005,
        )?.position;
        if (pickedFromRay && this._camera.isPositionVisible(pickedFromRay)) {
          return Cartesian3.clone(pickedFromRay);
        }
      } catch {
        // Fall back to depth/globe picking below.
      }
    }

    if (this._viewer.scene.pickPositionSupported) {
      try {
        const position = this._viewer.scene.pickPosition(screenPosition);
        if (position && this._camera.isPositionVisible(position)) {
          return position;
        }
      } catch {
        // Fall back to globe picking below.
      }
    }

    if (!ray) return null;

    try {
      const globePosition = this._viewer.scene.globe.pick(
        ray,
        this._viewer.scene,
      );
      return globePosition && this._camera.isPositionVisible(globePosition)
        ? globePosition
        : null;
    } catch {
      return null;
    }
  }

  private ensureMeasurementEndPreviewEntity() {
    if (
      !this._viewer ||
      this._entityMeasurementEnd ||
      this._toolStage.getSnapshot() !== "pickingEnd" ||
      !this._points.getSnapshot()[1]
    ) {
      return;
    }

    this._entityMeasurementEnd = this.addMeasurementEntityEnd();
  }

  private updateEndPointFromEvent(ev: PositionEvent) {
    const points = this._points.getSnapshot();
    const startPosition = points[0];
    let endPosition: Cartesian3;
    if (this._axisSnapEnabled && this._axisSnapGrid) {
      /** Snapping to project grid */
      endPosition =
        this.getProjectGridAxisSnapPosition(
          ev.position,
          startPosition,
          ev.screenPosition,
        ) ?? ev.position;
    } else if (
      this._surfaceDirectionSnapEnabled &&
      this._selectedSurfaceNormal
    ) {
      /** Snapping to surface direction */
      if (
        this._autoSnapToOpposingSurface.getSnapshot() &&
        !this._surfacePointOppositeNormal
      ) {
        this._surfacePointOppositeNormal =
          this.getOpposingSurfaceSnapPosition(startPosition);
      }

      if (
        this._autoSnapToOpposingSurface.getSnapshot() &&
        this._surfacePointOppositeNormal
      ) {
        endPosition = this._surfacePointOppositeNormal;
      } else {
        endPosition =
          this.getSurfaceDirectionSnapPosition(
            startPosition,
            ev.screenPosition,
          ) ?? ev.position;
      }
    } else if (
      this._surfaceDirectionSnapEnabled &&
      !this._selectedSurfaceNormal
    ) {
      /** Snapping to surface direction but surface normal not able to be determined */
      endPosition = ev.position;
    } else {
      /** Free movement */
      endPosition = ev.position;
    }
    this._points.set([startPosition, endPosition]);
  }

  private addEntityCursorPoint() {
    if (!this._viewer) return;
    return this._viewer.entities.add({
      position: new CallbackPositionProperty(
        () => this._mouseMoveEvent.position,
        false,
      ),
      show: false,
      point: {
        pixelSize: 5,
        color: Color.YELLOW,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      properties: {
        excludeFromPicking: true,
      },
    });
  }

  private removeEntityCursorPoint() {
    if (!this._viewer) return;
    this._viewer.entities.remove(this._entityCursorPoint);
    this._entityCursorPoint = null;
  }

  private updateMeasurementLabelValue() {
    const points = this._points.getSnapshot();
    const distance =
      points[0] && points[1] ? Cartesian3.distance(points[0], points[1]) : 0;
    const value = distance * this._unit.factor,
      text = `${value.toFixed(this._unit.decimals)} ${this._unit.shortname}`;
    this.updateLabelText(text);
    this._measurementValueText.set(text);
    this._changeEvent.emit({
      value,
      text,
    });
  }

  private getProjectGridAxisSnapPosition(
    position: Cartesian3,
    originWorldPosition: Cartesian3,
    hoveredWindowPosition: Cartesian2,
  ) {
    const directionalMatrix = getDirectionMatrix(
      this._axisSnapGrid.converter,
      originWorldPosition,
    );
    const northRadians = CesiumMath.toRadians(this._axisSnapGrid.northDegrees);

    const pickRay = this._viewer.camera.getPickRay(hoveredWindowPosition);
    if (!pickRay?.direction) return null;

    const b = pickRay.direction;
    const c = Cartesian3.subtract(
      this._viewer.camera.position,
      originWorldPosition,
      new Cartesian3(),
    );

    const normalDirection_x = VectorUtils.getDirectionalUnitVector(
      -northRadians,
      directionalMatrix,
      Axis.X,
    );
    const d_x = VectorUtils.getRaysClosestPoint(normalDirection_x, b, c);
    const position_x = Cartesian3.add(
      originWorldPosition,
      Cartesian3.multiplyByScalar(normalDirection_x, d_x, new Cartesian3()),
      new Cartesian3(),
    );

    const normalDirection_y = VectorUtils.getDirectionalUnitVector(
      -northRadians,
      directionalMatrix,
      Axis.Y,
    );
    const d_y = VectorUtils.getRaysClosestPoint(normalDirection_y, b, c);
    const position_y = Cartesian3.add(
      originWorldPosition,
      Cartesian3.multiplyByScalar(normalDirection_y, d_y, new Cartesian3()),
      new Cartesian3(),
    );

    const normalDirection_z = VectorUtils.getDirectionalUnitVector(
      -northRadians,
      directionalMatrix,
      Axis.Z,
    );
    const d_z = VectorUtils.getRaysClosestPoint(normalDirection_z, b, c);
    const position_z = Cartesian3.add(
      originWorldPosition,
      Cartesian3.multiplyByScalar(normalDirection_z, d_z, new Cartesian3()),
      new Cartesian3(),
    );

    return this.getClosestAxis(position, position_x, position_y, position_z);
  }

  private getClosestAxis(
    currentPosition: Cartesian3,
    x: Cartesian3,
    y: Cartesian3,
    z: Cartesian3,
  ) {
    const distances = [
      Cartesian3.distance(currentPosition, x),
      Cartesian3.distance(currentPosition, y),
      Cartesian3.distance(currentPosition, z),
    ];

    const closest = Math.min(...distances);
    const index = distances.indexOf(closest);

    switch (index) {
      case 0:
        return x;
      case 1:
        return y;
      case 2:
        return z;
      default:
        return currentPosition;
    }
  }

  private getOpposingSurfaceSnapPosition(
    originPosition: Cartesian3,
  ): Cartesian3 | null {
    if (!originPosition || !this._selectedSurfaceNormal || !this._viewer) {
      return null;
    }

    const objectsToExclude = this._viewer.entities.values.filter(
      (entity) => entity.properties?.excludeFromPicking,
    );
    const rayStartOffsetMeters = 0.05;
    const minimumSnapDistanceMeters = rayStartOffsetMeters * 2;
    const directions = [
      Cartesian3.clone(this._selectedSurfaceNormal),
      Cartesian3.negate(this._selectedSurfaceNormal, new Cartesian3()),
    ];

    for (const direction of directions) {
      try {
        const rayOrigin = Cartesian3.add(
          originPosition,
          Cartesian3.multiplyByScalar(
            direction,
            rayStartOffsetMeters,
            new Cartesian3(),
          ),
          new Cartesian3(),
        );
        const intersection = this._viewer.scene.pickFromRay(
          new Ray(rayOrigin, direction),
          objectsToExclude,
        );

        if (
          intersection?.position &&
          Cartesian3.distance(intersection.position, originPosition) >
            minimumSnapDistanceMeters
        ) {
          return Cartesian3.clone(intersection.position);
        }
      } catch {
        // Try the fallback direction below.
      }
    }

    return null;
  }

  /**Called when surface direction snap is enabled, but opposing surface snapping is disabled. */
  private getSurfaceDirectionSnapPosition(
    originPosition: Cartesian3,
    screenPosition: Cartesian2,
  ): Cartesian3 | null {
    if (!originPosition || !screenPosition || !this._selectedSurfaceNormal) {
      return null;
    }
    if (!this._viewer) return null;

    const pickRay = this._viewer.camera.getPickRay(screenPosition);
    if (!pickRay?.direction) {
      return null;
    }

    const delta = VectorUtils.getRaysClosestPoint(
      this._selectedSurfaceNormal,
      pickRay.direction,
      Cartesian3.subtract(
        this._viewer.camera.position,
        originPosition,
        new Cartesian3(),
      ),
    );
    if (!Number.isFinite(delta)) return null;

    return Cartesian3.add(
      originPosition,
      Cartesian3.multiplyByScalar(
        this._selectedSurfaceNormal,
        delta,
        new Cartesian3(),
      ),
      new Cartesian3(),
    );
  }

  private addEntityLabel() {
    if (!this._viewer) return;
    this._entityLabel = this._viewer.entities.add({
      name: "measurement-label",
      properties: {
        excludeFromPicking: true,
      },
    });
    const el = (this._elementLabelText = generateTextElement("0"));
    this._viewer.container.appendChild(el);

    this._detachPostRenderHandlerLabelUpdateFunc?.();
    this._detachPostRenderHandlerLabelUpdateFunc =
      this._viewer.scene.postRender.addEventListener((_, time: JulianDate) => {
        if (!this._entityLabel) return;
        const position = this._entityLabel.position?.getValue(time);
        this.updateLabelElementPosition(position);
      }, this);
  }

  private addMeasurementEntityStart() {
    if (!this._viewer) return;
    return this._viewer.entities.add({
      position: new CallbackPositionProperty(
        () => this._points.getSnapshot()[0],
        false,
      ),
      point: {
        pixelSize: 10,
        color: Color.YELLOW,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      properties: {
        excludeFromPicking: true,
      },
    });
  }

  private removeMeasurementEntityStart() {
    if (!this._viewer) return;
    this._viewer.entities.remove(this._entityMeasurementStart);
    this._entityMeasurementStart = null;
  }

  private addMeasurementEntityEnd() {
    if (!this._viewer) return;
    return this._viewer.entities.add({
      position: new CallbackPositionProperty(
        () => this._points.getSnapshot()[1],
        false,
      ),
      point: {
        pixelSize: 10,
        color: Color.YELLOW,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      properties: {
        excludeFromPicking: true,
      },
    });
  }

  private removeMeasurementEntityEnd() {
    if (!this._viewer) return;
    this._viewer.entities.remove(this._entityMeasurementEnd);
    this._entityMeasurementEnd = null;
  }

  private addEntityLine() {
    if (!this._viewer) return;
    const lineColor = Color.YELLOW;
    const material = new PolylineGlowMaterialProperty({
      glowPower: 0.15,
      color: lineColor,
    });
    this._entityLine = this._viewer.entities.add({
      name: "measurement-line",
      polyline: {
        positions: new CallbackProperty(
          () => this._points.getSnapshot(),
          false,
        ),
        width: 12,
        material,
        depthFailMaterial: lineColor,
        // Prevent longer measurements from curving towards the horizon.
        arcType: ArcType.NONE,
      },
      properties: {
        excludeFromPicking: true,
      },
    });
  }

  private removeEntityLine() {
    if (!this._viewer) return;
    this._viewer?.entities.remove(this._entityLine);
    this._entityLine = null;
  }

  private removeEntityLabel() {
    if (!this._viewer) return;
    this._viewer?.entities.remove(this._entityLabel);
    this._entityLabel = null;
    if (this._elementLabelText) {
      try {
        this._viewer?.container.removeChild(this._elementLabelText);
      } catch (error) {
        console.error("Error removing entity label", error);
      }
    }
    this._elementLabelText = null;
    this._detachPostRenderHandlerLabelUpdateFunc?.();
  }

  private updateLabelPosition() {
    const points = this._points.getSnapshot();
    let position = points[0];
    if (points[0] && points[1]) {
      position = Cartesian3.midpoint(points[0], points[1], new Cartesian3());
    }
    if (this._entityLabel) {
      this._entityLabel.position = new ConstantPositionProperty(position);
    }
    this.updateLabelElementPosition(position);
  }

  private updateLabelElementPosition(position: Cartesian3) {
    let show = false;
    const el = this._elementLabelText;
    if (position) {
      const position2d = SceneTransforms.worldToWindowCoordinates(
        this._viewer.scene,
        position,
      );
      if (position2d) {
        show = true;
        if (el) {
          const leftOffset = el.clientWidth / 2;
          const heightOffset = el.clientHeight / 2; //+ 15;
          el.style.left = position2d.x - leftOffset + "px";
          el.style.top = position2d.y - heightOffset + "px";
        }
      }
    }
    if (el) el.style.display = show ? "block" : "none";
  }

  private updateLabelText(value: string) {
    if (!this._elementLabelText) return;
    this._elementLabelText.innerHTML = value;
  }

  isActive(): boolean {
    return this._isActive;
  }

  activate(): void {
    this.setActive(true);
  }

  deactivate(): void {
    this.setActive(false);
  }

  capture(): MeasurementToolSnapshot {
    // Before the first click, the only point is the cursor preview that
    // follows the mouse. That isn't a measurement yet, so it isn't captured
    // and doesn't count as the tool being applied.
    const isAwaitingFirstClick =
      this._toolStage.getSnapshot() === "pickingStart";
    return {
      version: 2,
      points: isAwaitingFirstClick ? [] : this._points.getSnapshot(),
      unitShortName: this._unit.shortname,
    };
  }

  restore(state: MeasurementToolSnapshot | undefined): void {
    if (!state) return;
    state = migrateState(state);

    this.clear();

    this._points.set(state.points.map((p) => new Cartesian3(p.x, p.y, p.z)));
    const unit = linearUnits.find(
      (unit) => unit.shortname === state.unitShortName,
    );
    this.setUnits(unit);

    this._toolStage.set("complete");
    this.addEntityLine();
    this.addEntityLabel();
    this.updateLabelPosition();
    this.updateMeasurementLabelValue();
    this._entityMeasurementStart = this.addMeasurementEntityStart();
    this._entityMeasurementEnd = this.addMeasurementEntityEnd();
    this._viewer?.scene.requestRender();
  }

  isStateApplied(state: MeasurementToolSnapshot | undefined): boolean {
    return isMeasureStateApplied(state);
  }

  isMatchingState(state: MeasurementToolSnapshot | undefined): boolean {
    state = migrateState(state);
    return compareToolState(this.capture(), state, (s) =>
      this.isStateApplied(s),
    );
  }

  clear(): void {
    this._points.set([]);
    this._toolStage.set("pickingStart");
    this.cancelPendingPointerEndPointUpdate();
    this.cancelPendingPointerIdleUpdate();
    this.abortSelectedSurfaceNormalUpdate();
    this._selectedSurfaceNormal = null;
    this._measurementStartEvent = null;
    this._latestMouseScreenPosition = null;
    this._surfacePointOppositeNormal = null;
    this.removeEntityLine();
    this.removeEntityLabel();
    this.removeEntityCursorPoint();
    this.removeMeasurementEntityStart();
    this.removeMeasurementEntityEnd();
    const value = 0;
    const text = value.toFixed(this._unit.decimals);
    this.updateLabelText(text);
    this._changeEvent.emit({
      value,
      text,
    });
    this._viewer?.scene.requestRender();
  }
}
